import * as Sentry from '@sentry/react' // We need to use react Sentry here
import * as React from 'react'
import { isLoggedInQuerySelector } from 'auth/selectors'
import {
  usePaymentsActivateTrialMutation,
  usePaymentsExtendSubscriptionMutation,
} from '../gen/stripe'
import { TrialActivationReason } from '../gen/types'
import type { PaymentStatus } from '../gen/types'
import { userQueryFetcher } from 'auth/hooks'
import type { UpgradeSource } from '../upgrade-sources'
import { getActivateTrialPaymentResult } from '../lib/activate-trial/get-activate-trial-payment-result'

interface FailingReason {
  type:
    | 'noUser'
    | 'noAllAccess'
    | 'noStudioAi'
    | 'noSubscriptionId'
    | 'upgradeFailed'
    | 'extendFailed'
    | 'unknown'
  message: string
}

export interface ChargeReturn {
  type?: 'upgrade' | 'extend'
  paymentsStatus?: PaymentStatus
  message?: string | null
  nextPaymentDate?: string
  maxExtensionsReached?: boolean | null
  userBanned?: boolean | null
  error: FailingReason | null
}

interface UpgradeOrExtendSubscription {
  isProcessing: boolean
  charge: () => Promise<ChargeReturn> | ChargeReturn
}

export function useUpgradeOrExtendSubscription({
  subscriptionType = 'all-access',
  upgradeSource,
}: {
  subscriptionType?: 'all-access' | 'studio-ai'
  upgradeSource: UpgradeSource
}): UpgradeOrExtendSubscription {
  const [isProcessing, setIsProcessing] = React.useState(false)

  const activateTrialMutation = usePaymentsActivateTrialMutation({
    retry: 3,
    retryDelay: attempt => attempt * 1000,
  })

  const stripeExtendSubscriptionMutation = usePaymentsExtendSubscriptionMutation({
    retry: 3,
    retryDelay: attempt => attempt * 1000,
  })

  // oxlint-disable-next-line complexity
  const handleUpgradeOrExtend = React.useCallback(async (): Promise<ChargeReturn> => {
    setIsProcessing(true)
    const user = await userQueryFetcher()
    const isUserLoggedIn = isLoggedInQuerySelector(user)
    const userData = user?.me?.user

    //All Access
    const allAccessSubscriptionDetails = userData?.subscriptionAccess.allAccess
    const userHasAllAccess = allAccessSubscriptionDetails?.isSubscribed ?? false
    const userEligibleForExtension =
      userHasAllAccess && userData && !allAccessSubscriptionDetails?.isEligibleForYearlyUpgrade
    const isEligibleForAllAccessFreeTrial = allAccessSubscriptionDetails?.isEligibleForFreeTrial

    //Studio AI
    const studioAiSubscriptionDetails = userData?.subscriptionAccess.studioAi
    const userHasStudioAi = studioAiSubscriptionDetails?.isSubscribed ?? false
    const isEligibleForStudioAiFreeTrial = studioAiSubscriptionDetails?.isEligibleForFreeTrial
    const subscriptionToUpgrade =
      subscriptionType === 'all-access'
        ? allAccessSubscriptionDetails?.subscription
        : studioAiSubscriptionDetails?.subscription

    const stripeCustomer = userData?.stripeCustomer

    if (!isUserLoggedIn || !userData) {
      setIsProcessing(false)
      return {
        type: undefined,
        error: {
          type: 'noUser',
          message: '',
        },
        paymentsStatus: undefined,
        message: null,
      }
    }

    if (
      subscriptionType === 'all-access' &&
      (isEligibleForAllAccessFreeTrial || !userHasAllAccess)
    ) {
      setIsProcessing(false)
      return {
        type: undefined,
        error: {
          type: 'noAllAccess',
          message: '',
        },
        paymentsStatus: undefined,
        message: null,
      }
    }

    if (subscriptionType === 'studio-ai' && (isEligibleForStudioAiFreeTrial || !userHasStudioAi)) {
      setIsProcessing(false)
      return {
        type: undefined,
        error: {
          type: 'noStudioAi',
          message: '',
        },
        paymentsStatus: undefined,
        message: null,
      }
    }

    //================== EXTEND SUBSCRIPTION START ==================

    if (userEligibleForExtension) {
      //Get stripe subscription id to extend
      const stripeSubscriptionIdToExtend = allAccessSubscriptionDetails?.subscription?.id

      // Extend subscription via stripe
      if (stripeSubscriptionIdToExtend) {
        try {
          // extend subscription
          const extendResponse = await stripeExtendSubscriptionMutation.mutateAsync({
            stripeSubscriptionId: stripeSubscriptionIdToExtend,
            stripeAccount: stripeCustomer?.account,
          })

          const extendData = extendResponse.paymentsExtendSubscription

          if (!extendData) {
            Sentry.captureException(`Failed to extend subscription: No data returned`)
            return {
              type: 'extend',
              error: { type: 'extendFailed', message: '' },
              paymentsStatus: undefined,
              message: null,
            }
          }

          return {
            type: 'extend',
            error: null,
            paymentsStatus: extendData.paymentStatus,
            nextPaymentDate: extendData.nextPaymentDate,
            maxExtensionsReached: extendData.maxExtensionsReached,
            userBanned: extendData.userBanned,
            message: extendData.payInvoiceUrl,
          }
        } catch (error) {
          Sentry.captureException(
            new Error(
              `Failed to extend subscription - unknown error: ${new Error(
                (error as Error)?.message || (error as string),
              )}`,
            ),
          )
          return {
            type: 'extend',
            error: {
              type: 'unknown',
              message: (error as Error)?.message || (error as string),
            },
            paymentsStatus: undefined,
            message: null,
          }
        } finally {
          setIsProcessing(false)
        }
      }

      Sentry.captureException(`Failed to extend subscription: No subscription id found`)
      return {
        type: 'extend',
        error: { type: 'extendFailed', message: '' },
        paymentsStatus: undefined,
        message: null,
      }
    }
    //================== EXTEND SUBSCRIPTION END ==================

    //================== UPGRADE SUBSCRIPTION START ==================
    if (!subscriptionToUpgrade?.isTrial) {
      setIsProcessing(false)
      return {
        type: 'upgrade',
        error: { type: 'upgradeFailed', message: '' },
        paymentsStatus: undefined,
        message: null,
      }
    }

    try {
      //Get the subscriptionId to upgrade
      const stripeSubscriptionIdToUpgrade = subscriptionToUpgrade.id || null

      if (
        stripeSubscriptionIdToUpgrade === null ||
        stripeSubscriptionIdToUpgrade === undefined ||
        stripeSubscriptionIdToUpgrade.length === 0
      ) {
        setIsProcessing(false)
        return {
          type: 'upgrade',
          error: { type: 'noSubscriptionId', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      const activateTrialResponse = await activateTrialMutation.mutateAsync({
        input: {
          id: stripeSubscriptionIdToUpgrade,
          activationReason: TrialActivationReason.UserRequest,
          activationSource: upgradeSource,
        },
        applyBonus: false,
      })

      const activateTrialData = activateTrialResponse.paymentsActivateTrial
      const activateTrialResult = getActivateTrialPaymentResult(activateTrialData)

      if (!activateTrialData?.subscription && !activateTrialData?.errors?.length) {
        Sentry.captureException(`Failed to activate yearly subscription: No data returned`)
        return {
          type: 'upgrade',
          error: { type: 'upgradeFailed', message: '' },
          paymentsStatus: undefined,
          message: null,
        }
      }

      return {
        type: 'upgrade',
        error: null,
        paymentsStatus: activateTrialResult.paymentStatus,
        userBanned: null,
        message: activateTrialResult.hostedUrl,
      }
    } catch (error) {
      Sentry.captureException(
        new Error(
          `Failed to activate yearly subscription - unknown error: ${new Error(
            (error as Error)?.message || (error as string),
          )}`,
        ),
      )
      return {
        type: 'upgrade',
        error: {
          type: 'unknown',
          message: (error as Error)?.message || (error as string),
        },
        paymentsStatus: undefined,
        message: null,
      }
    } finally {
      setIsProcessing(false)
    }

    //================== UPGRADE SUBSCRIPTION END ==================
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionType, upgradeSource])

  return {
    isProcessing,
    charge: handleUpgradeOrExtend,
  }
}
